import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Plus, Search, Dumbbell, Pencil, Trash2, PlayCircle, FileText, Clock,
  Tag, X, Sparkles, BookOpen, Video, ExternalLink, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

const SPORTS = ['All', 'Football', 'Cricket', 'Hockey', 'Tennis', 'Badminton', 'Basketball', 'Swimming', 'Athletics'];
const CATEGORIES = ['warm-up', 'skill', 'tactical', 'conditioning', 'cool-down', 'general'];
const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];

const categoryMeta = {
  'warm-up': { color: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Warm-up' },
  'skill': { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Skill' },
  'tactical': { color: 'bg-purple-100 text-purple-800 border-purple-300', label: 'Tactical' },
  'conditioning': { color: 'bg-rose-100 text-rose-800 border-rose-300', label: 'Conditioning' },
  'cool-down': { color: 'bg-teal-100 text-teal-800 border-teal-300', label: 'Cool-down' },
  'general': { color: 'bg-slate-100 text-slate-700 border-slate-300', label: 'General' },
};

// Phase 260e — Registry-driven Standard Playbook
const STANDARD_CATEGORIES = ['primary', 'secondary', 'positioning', 'precision', 'simulation'];
const standardCategoryMeta = {
  primary:     { color: 'bg-rose-100 text-rose-800 border-rose-300',         label: 'Primary' },
  secondary:   { color: 'bg-amber-100 text-amber-800 border-amber-300',      label: 'Secondary' },
  positioning: { color: 'bg-blue-100 text-blue-800 border-blue-300',         label: 'Positioning' },
  precision:   { color: 'bg-purple-100 text-purple-800 border-purple-300',   label: 'Precision' },
  simulation:  { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Simulation' },
};

// Extract YouTube video ID from common URL formats; returns null when not embeddable.
const getYouTubeEmbedSrc = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.replace('/', '');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
      // /embed/<id> or /shorts/<id>
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0] === 'embed' && parts[1]) return `https://www.youtube.com/embed/${parts[1]}`;
      if (parts[0] === 'shorts' && parts[1]) return `https://www.youtube.com/embed/${parts[1]}`;
    }
  } catch (_) { /* fall through */ }
  return null;
};

const emptyForm = () => ({
  title: '',
  description: '',
  sport: '',
  category: 'general',
  duration_minutes: 15,
  difficulty: 'intermediate',
  video_url: '',
  attachment_url: '',
  equipment: '',
  tags: '',
});

export default function DrillLibrary() {
  const { user } = useAuth();
  const canEdit = ['academy_owner', 'coach', 'employee', 'super_admin'].includes(user?.role);

  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sport: 'All', category: 'all', search: '' });
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  // Phase 260e — Standard Playbook tab state (registry-derived, read-only)
  const [activeTab, setActiveTab] = useState('mine');
  const [standardDrills, setStandardDrills] = useState([]);
  const [standardLoading, setStandardLoading] = useState(false);
  const [standardLoaded, setStandardLoaded] = useState(false);
  const [standardFilters, setStandardFilters] = useState({ sport: 'All', category: 'all', search: '' });
  const [standardDetail, setStandardDetail] = useState(null);

  const fetchDrills = useCallback(async () => {
    try {
      const params = {};
      if (filters.sport && filters.sport !== 'All') params.sport = filters.sport;
      if (filters.category && filters.category !== 'all') params.category = filters.category;
      if (filters.search) params.search = filters.search;
      const res = await api.get('/drills', { params });
      setDrills(res.data || []);
    } catch (err) {
      toast.error('Failed to load drill library');
    } finally {
      setLoading(false);
    }
  }, [filters.sport, filters.category, filters.search]);

  useEffect(() => { fetchDrills(); }, [fetchDrills]);

  // Phase 260e — Lazy-load Standard Playbook only when the tab is first activated.
  // The registry has 120 drills, the payload is ~25KB — fetch once and cache locally.
  const fetchStandardDrills = useCallback(async () => {
    setStandardLoading(true);
    try {
      const res = await api.get('/sports/drills');
      setStandardDrills(res.data?.drills || []);
      setStandardLoaded(true);
    } catch (err) {
      toast.error('Failed to load Standard Playbook');
    } finally {
      setStandardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'standard' && !standardLoaded) fetchStandardDrills();
  }, [activeTab, standardLoaded, fetchStandardDrills]);

  const filteredStandardDrills = standardDrills.filter((d) => {
    if (standardFilters.sport !== 'All' && d.sport_display_name !== standardFilters.sport) return false;
    if (standardFilters.category !== 'all' && d.category !== standardFilters.category) return false;
    if (standardFilters.search) {
      const q = standardFilters.search.toLowerCase();
      if (!d.name?.toLowerCase().includes(q) && !d.sport_display_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowDialog(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      title: d.title || '',
      description: d.description || '',
      sport: d.sport || '',
      category: d.category || 'general',
      duration_minutes: d.duration_minutes || 15,
      difficulty: d.difficulty || 'intermediate',
      video_url: d.video_url || '',
      attachment_url: d.attachment_url || '',
      equipment: (d.equipment || []).join(', '),
      tags: (d.tags || []).join(', '),
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Drill title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        sport: form.sport.trim(),
        category: form.category,
        duration_minutes: parseInt(form.duration_minutes) || 15,
        difficulty: form.difficulty,
        video_url: form.video_url.trim(),
        attachment_url: form.attachment_url.trim(),
        equipment: form.equipment.split(',').map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.put(`/drills/${editingId}`, payload);
        toast.success('Drill updated');
      } else {
        await api.post('/drills', payload);
        toast.success('Drill added to library');
      }
      setShowDialog(false);
      setEditingId(null);
      setForm(emptyForm());
      fetchDrills();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save drill');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (drillId) => {
    if (!window.confirm('Delete this drill from the library? Any training sessions referencing it will have the link removed.')) return;
    try {
      await api.delete(`/drills/${drillId}`);
      toast.success('Drill deleted');
      fetchDrills();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return (
    <div className="space-y-4" data-testid="drill-library-loading">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="drill-library-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Dumbbell className="h-6 w-6 text-teal-600" /> Drill Library
          </h1>
          <p className="text-sm text-muted-foreground">{drills.length} custom drill{drills.length !== 1 ? 's' : ''} · 120 standard drills · reusable across training sessions</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="drill-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Drill
          </Button>
        )}
      </div>

      {/* Phase 260e — Tabs: My Library (custom academy drills) vs Standard Playbook (registry) */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="drill-tabs">
          <TabsTrigger value="mine" data-testid="drill-tab-mine">
            <Wrench className="h-3.5 w-3.5 mr-1.5" /> My Library
            <Badge variant="secondary" className="ml-2 text-[10px]">{drills.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="standard" data-testid="drill-tab-standard">
            <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Standard Playbook
            <Badge variant="secondary" className="ml-2 text-[10px]">120</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={filters.sport} onValueChange={(v) => setFilters({ ...filters, sport: v })}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="drill-filter-sport"><SelectValue /></SelectTrigger>
              <SelectContent>{SPORTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-40 h-9 text-xs" data-testid="drill-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{categoryMeta[c]?.label || c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-48 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search drills…"
                className="pl-9 h-9 text-xs"
                data-testid="drill-filter-search"
              />
            </div>
          </div>

          {/* Grid */}
          {drills.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>No drills found{filters.search || filters.sport !== 'All' || filters.category !== 'all' ? ' for the current filters.' : ' yet.'}</p>
                {canEdit && !filters.search && filters.sport === 'All' && filters.category === 'all' && (
                  <Button variant="outline" className="mt-4" onClick={openCreate} data-testid="drill-empty-add-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> Add your first drill
                  </Button>
                )}
                <p className="text-xs mt-4 opacity-70">Need inspiration? Switch to the <span className="font-medium">Standard Playbook</span> tab for 120 curated drills.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="drill-grid">
              {drills.map((d) => {
                const meta = categoryMeta[d.category] || categoryMeta.general;
                return (
                  <Card key={d.id} className="hover:shadow-md transition-shadow cursor-pointer group" data-testid={`drill-card-${d.id}`} onClick={() => setDetail(d)}>
                    <CardContent className="p-4 space-y-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm leading-snug line-clamp-2" data-testid={`drill-title-${d.id}`}>{d.title}</h3>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                            {d.sport && <Badge variant="outline" className="text-[10px]">{d.sport}</Badge>}
                            <Badge variant="outline" className="text-[10px] capitalize">{d.difficulty}</Badge>
                          </div>
                        </div>
                        {canEdit && (
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); openEdit(d); }} data-testid={`drill-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }} data-testid={`drill-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        )}
                      </div>
                      {d.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{d.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{d.duration_minutes || 15}m</span>
                        {d.video_url && <span className="flex items-center gap-1 text-rose-600"><PlayCircle className="h-3 w-3" />Video</span>}
                        {d.attachment_url && <span className="flex items-center gap-1 text-blue-600"><FileText className="h-3 w-3" />PDF</span>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="standard" className="space-y-4 mt-4">
          {/* Standard Playbook filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={standardFilters.sport} onValueChange={(v) => setStandardFilters({ ...standardFilters, sport: v })}>
              <SelectTrigger className="w-40 h-9 text-xs" data-testid="standard-filter-sport"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Sports</SelectItem>
                {['Football', 'Cricket', 'Field Hockey', 'Tennis', 'Badminton', 'Basketball', 'Swimming', 'Athletics'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={standardFilters.category} onValueChange={(v) => setStandardFilters({ ...standardFilters, category: v })}>
              <SelectTrigger className="w-44 h-9 text-xs" data-testid="standard-filter-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {STANDARD_CATEGORIES.map(c => <SelectItem key={c} value={c}>{standardCategoryMeta[c]?.label || c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-48 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={standardFilters.search}
                onChange={(e) => setStandardFilters({ ...standardFilters, search: e.target.value })}
                placeholder="Search standard drills…"
                className="pl-9 h-9 text-xs"
                data-testid="standard-filter-search"
              />
            </div>
            <Badge variant="outline" className="text-[10px]">
              {filteredStandardDrills.length} of {standardDrills.length} shown
            </Badge>
          </div>

          {standardLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}
            </div>
          ) : filteredStandardDrills.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>No drills match the current filters.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="standard-drill-grid">
              {filteredStandardDrills.map((d) => {
                const meta = standardCategoryMeta[d.category] || { color: 'bg-slate-100 text-slate-700 border-slate-300', label: d.category };
                return (
                  <Card
                    key={d.id}
                    className="hover:shadow-md transition-shadow cursor-pointer group"
                    data-testid={`standard-drill-card-${d.id}`}
                    onClick={() => setStandardDetail(d)}
                    style={d.color_accent ? { borderLeft: `3px solid ${d.color_accent}` } : undefined}
                  >
                    <CardContent className="p-4 space-y-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm leading-snug line-clamp-2" data-testid={`standard-drill-title-${d.id}`}>
                            <span className="mr-1">{d.sport_emoji}</span>{d.name}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Badge variant="outline" className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                            <Badge variant="outline" className="text-[10px]">{d.sport_display_name}</Badge>
                            {d.level && <Badge variant="outline" className="text-[10px] capitalize">{d.level}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t">
                        {d.duration_min && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{d.duration_min}m</span>}
                        {d.video_url
                          ? <span className="flex items-center gap-1 text-rose-600"><PlayCircle className="h-3 w-3" />Video</span>
                          : <span className="flex items-center gap-1 text-slate-500"><Search className="h-3 w-3" />YouTube</span>}
                        {(d.equipment?.length > 0) && <span className="flex items-center gap-1 text-blue-600"><Tag className="h-3 w-3" />{d.equipment.length} item{d.equipment.length > 1 ? 's' : ''}</span>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="drill-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.title}</DialogTitle>
            <DialogDescription>Drill details & resources</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className={categoryMeta[detail.category]?.color || ''}>{categoryMeta[detail.category]?.label}</Badge>
                {detail.sport && <Badge variant="outline">{detail.sport}</Badge>}
                <Badge variant="outline" className="capitalize">{detail.difficulty}</Badge>
                <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />{detail.duration_minutes}m</Badge>
              </div>
              {detail.description && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Description</p>
                  <p className="whitespace-pre-wrap">{detail.description}</p>
                </div>
              )}
              {(detail.equipment?.length > 0) && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Equipment</p>
                  <div className="flex flex-wrap gap-1">{detail.equipment.map((e) => <Badge key={e} variant="secondary" className="text-[10px]">{e}</Badge>)}</div>
                </div>
              )}
              {(detail.tags?.length > 0) && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Tags</p>
                  <div className="flex flex-wrap gap-1">{detail.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]"><Tag className="h-2.5 w-2.5 mr-0.5" />{t}</Badge>)}</div>
                </div>
              )}
              {detail.video_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Video</p>
                  <a href={detail.video_url} target="_blank" rel="noreferrer" className="text-rose-600 hover:underline flex items-center gap-1.5 text-sm" data-testid="drill-detail-video"><PlayCircle className="h-4 w-4" />Open video</a>
                </div>
              )}
              {detail.attachment_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Attachment</p>
                  <a href={detail.attachment_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1.5 text-sm" data-testid="drill-detail-attachment"><FileText className="h-4 w-4" />Open document</a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Standard Playbook detail dialog (Phase 260e) — read-only, registry-derived */}
      <Dialog open={!!standardDetail} onOpenChange={() => setStandardDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="standard-drill-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {standardDetail?.sport_emoji && <span>{standardDetail.sport_emoji}</span>}
              <span>{standardDetail?.name}</span>
            </DialogTitle>
            <DialogDescription>From the curated Standard Playbook · {standardDetail?.sport_display_name}</DialogDescription>
          </DialogHeader>
          {standardDetail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className={standardCategoryMeta[standardDetail.category]?.color || ''}>
                  {standardCategoryMeta[standardDetail.category]?.label || standardDetail.category}
                </Badge>
                <Badge variant="outline">{standardDetail.sport_display_name}</Badge>
                {standardDetail.level && <Badge variant="outline" className="capitalize">{standardDetail.level}</Badge>}
                {standardDetail.duration_min && <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />{standardDetail.duration_min}m</Badge>}
              </div>
              {(standardDetail.equipment?.length > 0) && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Equipment</p>
                  <div className="flex flex-wrap gap-1">{standardDetail.equipment.map((e) => <Badge key={e} variant="secondary" className="text-[10px]">{e}</Badge>)}</div>
                </div>
              )}
              {standardDetail.video_url ? (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> Video demo</p>
                  {getYouTubeEmbedSrc(standardDetail.video_url) ? (
                    <div className="relative w-full overflow-hidden rounded-lg border bg-slate-50" style={{ paddingTop: '56.25%' }}>
                      <iframe
                        title={standardDetail.name}
                        src={getYouTubeEmbedSrc(standardDetail.video_url)}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid="standard-drill-detail-video-embed"
                      />
                    </div>
                  ) : (
                    <a
                      href={standardDetail.video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-rose-600 hover:underline flex items-center gap-1.5 text-sm"
                      data-testid="standard-drill-detail-video-link"
                    >
                      <PlayCircle className="h-4 w-4" /> Open video <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ) : null}

              {/* Always-on YouTube search fallback (Phase 260f) — works for 100% of drills
                  with zero fabrication risk. */}
              {standardDetail.video_search_url && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <Search className="h-3.5 w-3.5" /> {standardDetail.video_url ? 'More demos' : 'Find demos on YouTube'}
                  </p>
                  <a
                    href={standardDetail.video_search_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50 transition-colors"
                    data-testid="standard-drill-detail-search-link"
                  >
                    <PlayCircle className="h-4 w-4" />
                    Search "{standardDetail.name}" on YouTube
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              <div className="pt-2 border-t text-[11px] text-muted-foreground">
                This drill is part of the curated Out-Play standard playbook. To customize and attach it to a training session,
                add it to <span className="font-medium">My Library</span>.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(v) => { setShowDialog(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
        <DialogContent
          className="max-w-xl max-h-[90vh] flex flex-col p-0 gap-0"
          data-testid="drill-form-dialog"
        >
          {/* Sticky header — never scrolls */}
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle>{editingId ? 'Edit Drill' : 'Add Drill to Library'}</DialogTitle>
            <DialogDescription>Reusable practice routines that can be attached to any training session.</DialogDescription>
          </DialogHeader>
          {/* Scrollable body — overflow contained inside the modal frame */}
          <div className="space-y-3 overflow-y-auto px-6 py-4 flex-1">
            <div className="space-y-1.5">
              <Label htmlFor="drill-title" className="text-xs">Title *</Label>
              <Input id="drill-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cone Dribble Weave" data-testid="drill-form-title" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="drill-desc" className="text-xs">Description</Label>
              <Textarea id="drill-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Step-by-step instructions, coaching cues, common mistakes…" rows={3} data-testid="drill-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Sport</Label>
                <Input value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} placeholder="Football, Hockey…" data-testid="drill-form-sport" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="drill-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{categoryMeta[c]?.label || c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Duration (min)</Label>
                <Input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="drill-form-duration" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Difficulty</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger data-testid="drill-form-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIFFICULTIES.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Video URL (YouTube / Vimeo / mp4)</Label>
              <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="drill-form-video" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Attachment URL (PDF / diagram)</Label>
              <Input value={form.attachment_url} onChange={(e) => setForm({ ...form, attachment_url: e.target.value })} placeholder="https://drive.google.com/…" data-testid="drill-form-attachment" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Equipment (comma-separated)</Label>
                <Input value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} placeholder="cones, balls, bibs" data-testid="drill-form-equipment" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="passing, agility, U-12" data-testid="drill-form-tags" />
              </div>
            </div>
          </div>
          {/* Sticky footer — actions always visible */}
          <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 shrink-0 bg-slate-50/60 dark:bg-slate-900/40">
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="drill-form-cancel"><X className="h-4 w-4 mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="drill-form-save">{saving ? 'Saving…' : (editingId ? 'Update Drill' : 'Add Drill')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
